import { wikiPageCollection } from "./wiki-page.collection";
import { useState } from "react";
import type { Collection } from "@tanstack/db";
import { useDbClient } from "@tanstack/react-db";
import type { WikiViewProps } from "@softmax/web-ux/wiki";
import { collectionError } from "@softmax/web-features/data";
import { communityAccess } from "@softmax/web-features/community";
import {
  wikiPermissionsCollection,
  wikiIndexCollection,
  wikiRevisionsCollection,
  type WikiIdentity,
} from "./wiki.collections";

type WikiRefreshRead = Pick<Collection, "status" | "health" | "reload">;

function refreshWikiRead({
  query,
  source,
}: {
  readonly query: WikiRefreshRead | undefined;
  readonly source: {
    readonly utils: { readonly refetch: () => Promise<unknown> };
  };
}): Promise<unknown> {
  return query?.status === "error" ? query.reload() : source.utils.refetch();
}

export function useWikiRefresh({
  scope,
  mode,
  pageId,
  permissions,
  page,
  index,
  revisions,
}: {
  readonly scope: WikiIdentity;
  readonly mode: WikiViewProps["mode"];
  readonly pageId: string | undefined;
  readonly permissions: WikiRefreshRead;
  readonly page: WikiRefreshRead | undefined;
  readonly index: WikiRefreshRead | undefined;
  readonly revisions: WikiRefreshRead | undefined;
}) {
  const db = useDbClient();
  const [refreshing, setRefreshing] = useState(false);

  async function refresh(resource: Parameters<WikiViewProps["onRefresh"]>[0]) {
    const signal = db.requireDependency<AbortController>("lifetime").signal;

    if (signal.aborted) return;

    setRefreshing(true);

    try {
      const permissionsSource = db.collection(wikiPermissionsCollection(scope));
      await Promise.allSettled([
        refreshWikiRead({ query: permissions, source: permissionsSource }),
      ]);

      if (signal.aborted || collectionError(permissionsSource.health)) return;

      const pageSource = db.collection(wikiPageCollection(scope));
      const indexSource = db.collection(wikiIndexCollection(scope));
      await Promise.allSettled([
        refreshWikiRead({ query: page, source: pageSource }),
        refreshWikiRead({ query: index, source: indexSource }),
      ]);

      if (signal.aborted || communityAccess([pageSource.health]).accessLost)
        return;

      const currentPage = pageSource.toArray[0];

      if (!currentPage) return;

      const historyNeeded =
        resource === "history" ||
        mode === "history" ||
        revisions?.health.hasErrors;

      if (!historyNeeded) return;

      const revisionsSource = db.collection(
        wikiRevisionsCollection({ ...scope, pageId: currentPage.id }),
      );
      await Promise.allSettled([
        refreshWikiRead({
          query: currentPage.id === pageId ? revisions : undefined,
          source: revisionsSource,
        }),
      ]);
    } finally {
      if (!signal.aborted) setRefreshing(false);
    }
  }

  return { refreshing, refresh };
}
